import React from "react";
import { motion } from "framer-motion";
import { Phone, MessageCircle, Star, Sparkles } from "lucide-react";

const PHONE_NUMBER = "+971541873527";
const WHATSAPP_LINK = `https://wa.me/971541873527?text=Hello%20Guru%20Maa,%20I%20would%20like%20to%20schedule%20a%20personalized%20astrology%20consultation.%20Please%20guide%20me.`;

// Premium Zodiac Wheel with layered rotation
const ZodiacWheel = () => (
  <div className="relative w-full h-full flex items-center justify-center">
    {/* Outer Ethereal Glow */}
    <div
      className="absolute rounded-full"
      style={{
        width: "130%",
        height: "130%",
        background:
          "radial-gradient(circle, rgba(212, 175, 55, 0.25) 0%, rgba(139, 92, 246, 0.1) 40%, transparent 70%)",
        filter: "blur(50px)",
        animation: "pulse-glow 5s ease-in-out infinite",
      }}
    />

    {/* Secondary Glow Ring */}
    <div
      className="absolute rounded-full"
      style={{
        width: "115%",
        height: "115%",
        background:
          "radial-gradient(circle, rgba(212, 175, 55, 0.2) 0%, transparent 60%)",
        filter: "blur(30px)",
      }}
    />

    {/* Outer Decorative Ring - Slowest (50s) */}
    <div
      className="absolute rounded-full"
      style={{
        width: "110%",
        height: "110%",
        border: "1px solid rgba(212, 175, 55, 0.2)",
        boxShadow: "0 0 20px rgba(212, 175, 55, 0.1)",
        animation: "celestial-spin 50s linear infinite",
      }}
    />

    {/* Main Zodiac Wheel Image - Slow Rotation (40s) */}
    <motion.div
      className="relative z-10"
      style={{
        width: "100%",
        height: "100%",
        animation: "celestial-spin 40s linear infinite",
      }}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1 }}
    >
      <img
        src="../../../images/astrowheel.webp"
        alt="Zodiac Wheel - Astrological Chart"
        className="w-full h-full object-contain"
        style={{
          filter:
            "drop-shadow(0 0 40px rgba(212, 175, 55, 0.5)) drop-shadow(0 0 80px rgba(212, 175, 55, 0.25))",
        }}
        data-testid="zodiac-wheel-image"
      />
    </motion.div>

    {/* Inner Glow Overlay */}
    <div
      className="absolute rounded-full pointer-events-none z-20"
      style={{
        width: "50%",
        height: "50%",
        background:
          "radial-gradient(circle, rgba(252, 211, 77, 0.1) 0%, transparent 70%)",
        animation: "pulse-subtle 4s ease-in-out infinite",
      }}
    />
  </div>
);

const Hero = () => {
  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.pexels.com/photos/6138036/pexels-photo-6138036.jpeg"
          alt="Cosmic Background"
          className="w-full h-full object-cover opacity-40"
        />
        <div className="hero-gradient absolute inset-0" />
      </div>

      {/* Cosmic Glow Effects */}
      <div className="cosmic-glow absolute top-1/4 left-1/4 opacity-50" />
      <div className="cosmic-glow absolute bottom-1/4 right-1/4 opacity-30" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="flex-1 text-center lg:text-left"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="flex items-center justify-center lg:justify-start gap-2 mb-6"
            >
              <Sparkles className="text-[#D4AF37]" size={20} />
              <span className="text-[#D4AF37] text-sm uppercase tracking-widest font-medium">
                Trusted Astrologer Since 2009
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
            >
              Unlock Your{" "}
              <span className="text-gold-gradient">Cosmic Destiny</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-[#94A3B8] text-base md:text-lg mb-4 max-w-xl mx-auto lg:mx-0"
            >
              Experience transformative guidance through ancient Vedic
              astrology. Expert consultation for life's most important decisions
              by Guru Maa Pooja Sharma.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-[#D4AF37] text-lg md:text-xl font-medium mb-8 italic"
            >
              "Discover the Truth Written in Your Stars"
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
            >
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 whatsapp-btn px-8 py-4 rounded-full text-lg font-bold shadow-lg"
                data-testid="hero-whatsapp-btn"
              >
                <MessageCircle size={22} />
                <span>Chat on WhatsApp</span>
              </a>
              <a
                href={`tel:${PHONE_NUMBER}`}
                className="flex items-center justify-center gap-3 cta-gold"
                data-testid="hero-call-btn"
              >
                <Phone size={22} />
                <span>Book Consultation</span>
              </a>
            </motion.div>

            {/* Trust Badges */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="flex items-center justify-center lg:justify-start gap-6 mt-10"
            >
              <div className="flex items-center gap-2">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      size={16}
                      fill="#D4AF37"
                      className="text-[#D4AF37]"
                    />
                  ))}
                </div>
                <span className="text-[#94A3B8] text-sm">4.9/5 Rating</span>
              </div>
              <div className="h-4 w-px bg-white/20" />
              <span className="text-[#94A3B8] text-sm">
                10,000+ Consultations
              </span>
            </motion.div>
          </motion.div>

          {/* Zodiac Wheel Animation */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="relative flex-shrink-0"
          >
            <div className="zodiac-wheel-container relative flex items-center justify-center">
              <ZodiacWheel />

              {/* Floating decorative elements */}
              <motion.div
                animate={{ y: [-10, 10, -10], opacity: [0.4, 0.8, 0.4] }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="absolute -top-4 -right-4 text-[#D4AF37] text-3xl"
                style={{ textShadow: "0 0 15px rgba(212, 175, 55, 0.8)" }}
              >
                ✨
              </motion.div>
              <motion.div
                animate={{ y: [10, -10, 10], opacity: [0.3, 0.7, 0.3] }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="absolute -bottom-4 -left-4 text-[#8B5CF6] text-2xl"
                style={{ textShadow: "0 0 12px rgba(139, 92, 246, 0.8)" }}
              >
                ☽
              </motion.div>
              <motion.div
                animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.9, 0.5] }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="absolute top-1/4 -left-8 text-[#FCD34D] text-xl"
                style={{ textShadow: "0 0 10px rgba(252, 211, 77, 0.8)" }}
              >
                ⭐
              </motion.div>
              <motion.div
                animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.8, 0.4] }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1,
                }}
                className="absolute bottom-1/4 -right-6 text-[#D4AF37] text-lg"
                style={{ textShadow: "0 0 10px rgba(212, 175, 55, 0.8)" }}
              >
                ✦
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border-2 border-[#D4AF37]/50 rounded-full flex justify-center pt-2"
        >
          <div className="w-1.5 h-3 bg-[#D4AF37] rounded-full" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
